import React from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Controller, FormContext, useForm } from 'react-hook-form'
import clsx from 'clsx'
import { isEmpty, omitBy } from 'lodash'
import { Alert } from '@material-ui/lab'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core'

import { FileUploadInput, JoditEditor, Label, SectionCard } from 'components'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import schema from './schema'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const AssessmentForm = ({ setLoading = () => {} }) => {
  const classes = useStyles()
  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      kind: undefined,
      content: '',
      fileUrl: '',
      attachments: null,
    },
  })
  const snackbar = useSnackbar()
  const history = useHistory()

  const { errors, control, handleSubmit, watch } = formMethods

  const {
    DUE_DILIGENCE_FILE_TYPES,
    DEFAULT_ERRO_MESSAGE,
    PRIVATE_VISIBILITY,
    DUE_DILIGENCE,
  } = constants.technicalFaqs

  const { TICKET_TYPE } = constants.tickets

  const { content, kind, ...formValues } = watch()

  const isAttachments = kind === 'attach'
  const isLink = kind === 'link'

  const hasContent = helpers.validates.jodit.hasContent(content)
  const isEmptyForm = isEmpty(omitBy(formValues, isEmpty)) && !hasContent

  const handleGoBack = () => history.goBack()

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const { content, kind, ...dataRest } = data

      const filteredDataRest = omitBy(dataRest, isEmpty)

      await service.dponet.juridicTickets.create({
        ...filteredDataRest,
        ...(helpers.validates.jodit.hasContent(content) && {
          content: content,
        }),
        visibilityType: PRIVATE_VISIBILITY,
        title: DUE_DILIGENCE,
        ticketType: TICKET_TYPE.JURIDIC_TICKET_TYPE,
      })

      snackbar.open({
        message: 'Solicitação enviada com sucesso!',
        variant: 'success',
      })
      history.goBack()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(
            error?.response?.data?.error,
            false,
          ) || DEFAULT_ERRO_MESSAGE,
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <SectionCard title="Informações para o parecer">
      <FormContext {...formMethods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
        >
          <Alert severity="info">
            Este formulário é exclusivo para as avaliações de terceiros que
            foram recebidas pela organização.
            <Typography variant="body2">
              Para avaliar um terceiro, acesse o&nbsp;
              <Link
                to={routes.thirdPartyManagements.mySuppliers.all}
                className={classes.link}
              >
                <strong>módulo de gestão de fornecedores.</strong>
              </Link>
            </Typography>
          </Alert>
          <Controller
            as={
              <FormControl>
                <Label
                  title="Formato do documento"
                  description="Escolha como deseja enviar a avaliação recebida: como arquivo ou como link de acesso."
                />
                <RadioGroup
                  className={clsx({ [classes.error]: errors?.kind })}
                  row
                >
                  <FormControlLabel
                    value="attach"
                    control={
                      <Radio
                        className={clsx({ [classes.error]: errors?.kind })}
                      />
                    }
                    label="Enviar arquivo"
                  />
                  <FormControlLabel
                    value="link"
                    control={
                      <Radio
                        className={clsx({ [classes.error]: errors?.kind })}
                      />
                    }
                    label="Informar link"
                  />
                </RadioGroup>
                {errors?.kind && (
                  <FormHelperText error={!!errors?.kind}>
                    {errors?.kind?.message}
                  </FormHelperText>
                )}
              </FormControl>
            }
            control={control}
            name="kind"
          />
          {isAttachments && (
            <Box>
              <FileUploadInput
                title="Arraste e solte ou selecione a avaliação a ser anexada"
                controlName="attachments"
                accept={DUE_DILIGENCE_FILE_TYPES}
                hasDownloadFile
                multiple
                controllerBar
              />
            </Box>
          )}
          {isLink && (
            <Box>
              <Label
                title="Link do documento"
                description="Caso o documento esteja disponível online, cole o link aqui."
                item
                xs={12}
              />
              <Controller
                name="fileUrl"
                control={control}
                as={
                  <TextField
                    color="primary"
                    variant="outlined"
                    type="url"
                    error={!!errors.fileUrl}
                    helperText={errors?.fileUrl?.message}
                    fullWidth
                  />
                }
              />
            </Box>
          )}
          <Box>
            <Label
              title="Informações adicionais"
              description="Caso tenha alguma observação adicional, por favor, informe aqui."
              item
              xs={12}
            />
            <Controller
              control={control}
              as={
                <JoditEditor.Editor
                  error={!!errors?.content}
                  helperText={errors?.content?.message}
                />
              }
              name="content"
              mode="onBlur"
            />
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            gridGap={theme.spacing(2)}
          >
            <Button variant="contained" onClick={handleGoBack}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isEmptyForm}
            >
              Solicitar
            </Button>
          </Box>
        </Box>
      </FormContext>
    </SectionCard>
  )
}

export default AssessmentForm
